import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Input, Typography, Tag, Spin, Empty, Segmented, Space, Checkbox, Button, Tooltip, message, Table, Popover, Modal, Alert } from 'antd';
import {
    SearchOutlined,
    AppstoreOutlined,
    LinkOutlined,
    DeleteOutlined,
    SettingOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    PauseCircleOutlined,
    HourglassOutlined,
    MinusCircleOutlined,
    TableOutlined,
    CalendarOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { EntityImage } from '../Entities/EntityImage';
import ConnectionEditModal from '../Entities/ConnectionEditModal';
import { useApi } from '../../hooks/use_api';
import { useAuth } from '../../context/AuthContext';
import { useSuperAdmins } from '../../hooks/use_super_admins';
import { useNodeInfo } from '../../hooks/use_node_info';
import { buildBrowseQuery, parseBrowseQuery, evaluateBrowseFilter, hasFilterClauses, isQueryExpression } from '../../utils/browse_query';
import { CONNECTION_STATES, stateOf, buildEdgeRows, edgeCalendarItems } from '../../utils/connections';
import { TABLE_COMPONENTS, compareNullsLast, textSearchDropdown, loadStoredJson, saveStoredJson } from './browse_table_helpers';
import BrowseCalendar, { windowLabel } from './BrowseCalendar';
import './Homepage.css';

const { Text } = Typography;

/**
 * Browse Edges — the homepage's sibling for the other half of the graph.
 * Browse lists the nodes (entities); this lists the edges between them
 * (connections), in the same table with the same features: tabs, column
 * filters and sort, a column chooser, resizable columns, the query bar and
 * its shareable ?q= link, and a bulk action bar for superadmins. Everything a
 * row shows is read off GET /api/connections plus the entity listing; its
 * state is worked out client-side (utils/connections.js). No search box up
 * top: the query bar is the search. The calendar view lays the same rows
 * over a month: a term over its window, a pause on the day it lifts.
 */

// Per-viewer column visibility and widths, browser-local like the theme and
// kept apart from the homepage's so each table remembers its own layout.
const COLUMN_PREFS_KEY = 'browse.edges.tableColumns';
const COLUMN_WIDTHS_KEY = 'browse.edges.tableColumnWidths';

// Per-viewer choice of table or calendar, browser-local like the column
// prefs: a view is how you like to look at the rows, not part of a query, so
// it stays out of ?q= and off the shareable link.
const VIEW_MODE_KEY = 'browse.edges.viewMode';
const VIEW_MODES = ['table', 'calendar'];

const STATE_ICONS = {
    live: <CheckCircleOutlined />,
    pending: <ClockCircleOutlined />,
    paused: <PauseCircleOutlined />,
    before: <HourglassOutlined />,
    after: <MinusCircleOutlined />,
};

// The tabs narrow by state, the way browse's narrow by entity type: Pending
// is what an admin comes here to action, Live is the graph as it counts now.
const STATE_TABS = [
    { value: 'all', label: 'All', icon: <AppstoreOutlined /> },
    ...CONNECTION_STATES.map(s => ({ value: s.value, label: s.label, icon: STATE_ICONS[s.value] })),
];

const ROLE_FILTERS = [
    { text: 'Member', value: 'member' },
    { text: 'Admin', value: 'admin' },
    { text: 'Installed', value: 'installed' },
];

// Shown under the query bar while it has focus — same grammar as browse
// (utils/browse_query.js), with this table's columns in the examples.
const QUERY_HELP = [
    ['gardening', 'Plain words match either end\'s name — they expand to the two clauses below, OR\'d.'],
    ['Entity A contains "ali"', 'Literal substring, case-insensitive. Entity B likewise.'],
    ['Role = "Admin"', 'One exact value, chosen from that column\'s own options.'],
    ['State = "Live" OR "Paused"', 'Several values from one column.'],
    ['… AND …', 'Both must hold. This is what the column filters build.'],
    ['… OR …', 'Either may hold — the one thing a column filter can\'t express.'],
    ['ORDER BY Created DESC', 'Sort. ASC to reverse; comma-separate to break ties.'],
    ['SHOW "Entity A", "Entity B", "State"', 'Choose which columns are on screen.'],
];

const isGroup = e => e.entity_type === 'context' || e.entity_type === 'relationship';
const stateIndex = value => CONNECTION_STATES.findIndex(s => s.value === value);
const timeKey = iso => (iso ? dayjs(iso).valueOf() : null);
const nameMatches = (values, name) => values.some(v => (name || '').toLowerCase().includes(String(v).toLowerCase()));
const filterIcon = filtered => <SearchOutlined style={{ color: filtered ? 'var(--ant-color-primary)' : undefined }} />;

// One end of an edge: its picture and its name. The name opens the entity;
// the rest of the row opens the connection itself, so the click stops here.
function EndCell({ entity, onOpen }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <EntityImage entity={entity} size={24} />
            <Typography.Link strong ellipsis onClick={e => { e.stopPropagation(); onOpen(entity); }}>
                {entity.name}
            </Typography.Link>
        </div>
    );
}

function BrowseEdges() {
    const navigate = useNavigate();
    const api = useApi();
    const { user } = useAuth();
    const { isServerAdmin, loading: superAdminsLoading } = useSuperAdmins();
    const { featureEntityTypeIdentifiers } = useNodeInfo();

    const [connections, setConnections] = useState([]);
    const [connectionsLoading, setConnectionsLoading] = useState(true);
    const [entities, setEntities] = useState([]);
    const [entitiesLoading, setEntitiesLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const fetchConnections = useCallback(() => {
        setConnectionsLoading(true);
        return api.get('/api/connections')
            .then(data => { setConnections(Array.isArray(data) ? data : []); setLoadError(null); })
            .catch(err => { setLoadError(err.message); })
            .finally(() => { setConnectionsLoading(false); });
    }, [api]);

    useEffect(() => { fetchConnections(); }, [fetchConnections]);

    // The ends come from the listing browse shows: listed entities, or every
    // entity for a superadmin (who also sees unlisted people and the hidden
    // relationships behind direct messages there). An edge whose end isn't in
    // it is left out, so what's browsable here is exactly what's browsable
    // there. Waits for the superadmin check so the listing is fetched once,
    // with the right scope, instead of twice.
    useEffect(() => {
        if (superAdminsLoading) return undefined;
        let alive = true;
        setEntitiesLoading(true);
        api.get('/api/entities', { params: isServerAdmin ? {} : { listed: true } })
            .then(data => { if (alive) setEntities(Array.isArray(data) ? data : []); })
            .catch(err => { if (alive) setLoadError(err.message); })
            .finally(() => { if (alive) setEntitiesLoading(false); });
        return () => { alive = false; };
    }, [api, isServerAdmin, superAdminsLoading]);

    const loading = connectionsLoading || entitiesLoading;

    const entityById = useMemo(() => new Map(entities.map(e => [e.id, e])), [entities]);
    const edges = useMemo(() => buildEdgeRows(connections, entityById), [connections, entityById]);

    const [stateFilter, setStateFilter] = useState('all');
    const [viewMode, setViewMode] = useState(() => {
        const saved = loadStoredJson(VIEW_MODE_KEY, 'table');
        return VIEW_MODES.includes(saved) ? saved : 'table';
    });
    const setViewModePersisted = (mode) => {
        setViewMode(mode);
        saveStoredJson(VIEW_MODE_KEY, mode);
    };
    // Newest first, like browse.
    const filteredEdges = useMemo(() => {
        const rows = stateFilter === 'all' ? edges : edges.filter(e => e.state === stateFilter);
        return [...rows].sort((a, b) => b.id - a.id);
    }, [edges, stateFilter]);

    // Row selection for the superadmin bulk action bar. Cleared on tab change
    // so stale ids from a different tab's rows never linger.
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkActionBusy, setBulkActionBusy] = useState(false);
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

    const [columnPrefs, setColumnPrefs] = useState(() => loadStoredJson(COLUMN_PREFS_KEY, {}));
    const [columnWidths, setColumnWidths] = useState(() => loadStoredJson(COLUMN_WIDTHS_KEY, {}));

    // Filters in disjunctive normal form: OR branches, each an AND of
    // per-column clauses. `[{}]` is no filter. Column dropdowns only ever edit
    // branch 0; extra branches arrive from the query bar or a shared link.
    const [filterGroups, setFilterGroups] = useState([{}]);
    const applyDropdownFilters = (filters) => setFilterGroups(prev => {
        const branch = { ...prev[0] };
        for (const [key, values] of Object.entries(filters)) {
            if (Array.isArray(values) && values.length > 0) branch[key] = values; else delete branch[key];
        }
        return [branch, ...prev.slice(1)];
    });
    const dropFilterColumn = (key) => setFilterGroups(prev => prev.map(({ [key]: _dropped, ...rest }) => rest));
    // The view this page was opened with, if it came from a shared link. Read
    // once at mount (before the sync-out effect can overwrite ?q=) and cleared
    // as soon as it's been applied.
    const [searchParams, setSearchParams] = useSearchParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const [pendingQuery, setPendingQuery] = useState(() => searchParams.get('q'));
    // { [columnKey]: 'ascend' | 'descend' } — controlled so the bar can read it.
    const [tableSorters, setTableSorters] = useState({});
    const applySorter = (sorter) => setTableSorters(Object.fromEntries(
        [].concat(sorter)
            .filter(s => s?.order && (s.columnKey ?? s.field))
            .map(s => [s.columnKey ?? s.field, s.order]),
    ));
    // Column prefs only join the shareable view once touched here (or arrived
    // in a link) — otherwise every visit would carry a SHOW clause it never chose.
    const [columnsTouched, setColumnsTouched] = useState(false);
    const setColumnPrefsPersisted = (key, visible) => setColumnPrefs(prev => {
        const next = { ...prev, [key]: visible };
        saveStoredJson(COLUMN_PREFS_KEY, next);
        return next;
    });
    const setColumnVisible = (key, visible) => {
        setColumnsTouched(true);
        if (!visible) {
            dropFilterColumn(key);
            setTableSorters(prev => { const { [key]: _dropped, ...rest } = prev; return rest; });
        }
        setColumnPrefsPersisted(key, visible);
    };

    const openEntity = useCallback(entity => navigate(`/entity/${entity.id}`), [navigate]);

    // The kinds actually present, for the Kind column's options.
    const kinds = useMemo(() => [...new Set(filteredEdges.map(e => e.kind))].sort(), [filteredEdges]);

    const tableColumns = useMemo(() => [
        {
            title: 'Entity A',
            key: 'a',
            // Both ends identify the row, so neither can be hidden.
            alwaysVisible: true,
            sorter: (x, y) => (x.a.name || '').localeCompare(y.a.name || ''),
            filterDropdown: textSearchDropdown('Search entity A'),
            filterIcon,
            matches: (values, edge) => nameMatches(values, edge.a.name),
            render: (_, edge) => <EndCell entity={edge.a} onOpen={openEntity} />,
        },
        {
            title: 'Entity B',
            key: 'b',
            alwaysVisible: true,
            sorter: (x, y) => (x.b.name || '').localeCompare(y.b.name || ''),
            filterDropdown: textSearchDropdown('Search entity B'),
            filterIcon,
            matches: (values, edge) => nameMatches(values, edge.b.name),
            render: (_, edge) => <EndCell entity={edge.b} onOpen={openEntity} />,
        },
        featureEntityTypeIdentifiers && {
            title: 'Kind',
            key: 'kind',
            width: 200,
            defaultHidden: true,
            sorter: (x, y) => x.kind.localeCompare(y.kind),
            filters: kinds.map(k => ({ text: k, value: k })),
            matches: (values, edge) => values.includes(edge.kind),
            render: (_, edge) => <Text type="secondary" style={{ textTransform: 'capitalize' }}>{edge.kind}</Text>,
        },
        {
            title: 'Role',
            key: 'role',
            width: 120,
            sorter: (x, y) => (x.role || '').localeCompare(y.role || ''),
            filters: ROLE_FILTERS,
            matches: (values, edge) => values.includes(edge.role),
            render: (_, edge) => (edge.role
                ? <Tag style={{ textTransform: 'capitalize' }}>{edge.role}</Tag>
                : <Text type="secondary">—</Text>),
        },
        {
            title: 'State',
            key: 'state',
            width: 130,
            sorter: (x, y) => stateIndex(x.state) - stateIndex(y.state),
            filters: CONNECTION_STATES.map(s => ({ text: s.label, value: s.value })),
            matches: (values, edge) => values.includes(edge.state),
            render: (_, edge) => {
                const s = stateOf(edge.state);
                const until = edge.state === 'paused' && edge.paused_until
                    ? `Until ${dayjs(edge.paused_until).format('D MMM YYYY')}`
                    : null;
                return <Tooltip title={until}><Tag color={s.color}>{s.label}</Tag></Tooltip>;
            },
        },
        {
            // A term (todo D12): the window the bond counts inside.
            title: 'When',
            key: 'window',
            width: 220,
            defaultHidden: true,
            sorter: (x, y) => compareNullsLast(timeKey(x.starts_at || x.ends_at), timeKey(y.starts_at || y.ends_at)),
            render: (_, edge) => <Text type="secondary">{edge.starts_at || edge.ends_at ? windowLabel(edge) : '—'}</Text>,
        },
        {
            title: 'Created',
            key: 'created',
            width: 130,
            defaultHidden: true,
            sorter: (x, y) => compareNullsLast(timeKey(x.created_at), timeKey(y.created_at)),
            render: (_, edge) => <Text type="secondary">{edge.created_at ? dayjs(edge.created_at).format('D MMM YYYY') : '—'}</Text>,
        },
        {
            title: 'Created by',
            key: 'created_by',
            defaultHidden: true,
            sorter: (x, y) => compareNullsLast(x.creator?.name ?? null, y.creator?.name ?? null),
            filterDropdown: textSearchDropdown('Search creator'),
            filterIcon,
            matches: (values, edge) => nameMatches(values, edge.creator?.name),
            render: (_, edge) => (edge.creator
                ? <EndCell entity={edge.creator} onOpen={openEntity} />
                : <Text type="secondary">—</Text>),
        },
        // `filteredValue` and `sortOrder` are only about what the header UI
        // shows — filtering itself happens in `visibleEdges`, because antd's
        // per-column onFilter can only ever AND columns together.
    ].filter(Boolean).map(col => ({
        ...col,
        filteredValue: filterGroups[0]?.[col.key] ?? null,
        ...(col.sorter && { sortOrder: tableSorters[col.key] ?? null }),
    })),
    [featureEntityTypeIdentifiers, kinds, filterGroups, tableSorters, openEntity]);

    // OR across branches, AND within one. An unknown column lets the row
    // through rather than emptying the table.
    const visibleEdges = useMemo(() => {
        const matchers = Object.fromEntries(tableColumns.map(col => [col.key, col.matches]));
        return filteredEdges.filter(edge => evaluateBrowseFilter(
            edge, filterGroups, (key, values, row) => matchers[key]?.(values, row) ?? true,
        ));
    }, [tableColumns, filteredEdges, filterGroups]);

    // The calendar draws the rows the table would show, from the same filter.
    const calendarItems = useMemo(() => edgeCalendarItems(visibleEdges), [visibleEdges]);

    const isColumnVisible = col => col.alwaysVisible || (columnPrefs[col.key] ?? !col.defaultHidden);
    // Widths are layered on here rather than in tableColumns, so a drag only
    // re-lays the table out and never re-runs the filter/query machinery.
    // Dragging updates state on every move; storage is written once, on
    // release. A double-click on the handle drops the column back to default.
    const visibleColumns = useMemo(
        () => tableColumns.filter(isColumnVisible).map(({ defaultHidden, alwaysVisible, ...col }) => ({
            ...col,
            width: columnWidths[col.key] ?? col.width,
            onHeaderCell: () => ({
                onResize: w => setColumnWidths(prev => ({ ...prev, [col.key]: w })),
                onResizeEnd: w => setColumnWidths(prev => {
                    const next = { ...prev };
                    if (w == null) delete next[col.key]; else next[col.key] = w;
                    saveStoredJson(COLUMN_WIDTHS_KEY, next);
                    return next;
                }),
            }),
        })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tableColumns, columnPrefs, columnWidths],
    );

    // The whole view as one canonical query string: what the banner shows and
    // what rides in ?q=. See utils/browse_query.js for the grammar.
    const tabLabel = stateFilter === 'all' ? null : (STATE_TABS.find(t => t.value === stateFilter)?.label ?? null);
    const visibleTitles = useMemo(() => (
        columnsTouched && tableColumns.some(col => isColumnVisible(col) !== !col.defaultHidden)
            ? tableColumns.filter(isColumnVisible).map(col => col.title)
            : null
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [tableColumns, columnPrefs, columnsTouched]);

    // A tab on its own isn't a query; it joins the string only once there's
    // something for it to scope. `tabInQuery` records whether it did, so
    // editing the text can tell "never shown" from "deleted".
    const [querySummary, tabInQuery] = useMemo(() => {
        const bare = !hasFilterClauses(filterGroups)
            && Object.keys(tableSorters).length === 0
            && !visibleTitles;
        return [
            buildBrowseQuery({
                columns: tableColumns,
                groups: filterGroups,
                sorters: tableSorters,
                tabLabel: bare ? null : tabLabel,
                visibleTitles,
            }),
            !bare && !!tabLabel,
        ];
    }, [tableColumns, filterGroups, tableSorters, tabLabel, visibleTitles]);

    // Mirror the view into ?q= so the address bar is always the shareable link.
    useEffect(() => {
        if (pendingQuery !== null) return;
        if (searchParams.get('q') === (querySummary || null)) return;
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (querySummary) next.set('q', querySummary); else next.delete('q');
            return next;
        }, { replace: true });
    }, [querySummary, pendingQuery, searchParams, setSearchParams]);

    const applyQuery = useCallback((text) => {
        const parsed = parseBrowseQuery(text, { columns: tableColumns, tabs: STATE_TABS });
        // The text is the whole state, so a Tab clause that was there and is
        // now gone means the reader deleted it: back to All. One that was never
        // shown leaves the tab alone. SHOW's absence always means "leave
        // columns alone", since resetting them would throw away a saved layout.
        setStateFilter(parsed.tab ?? (tabInQuery ? 'all' : stateFilter));
        setFilterGroups(parsed.groups);
        setTableSorters(parsed.sorters);
        if (parsed.visibleKeys) {
            // Applied for this visit only — a link someone shared shouldn't
            // permanently rewrite the recipient's saved column preferences.
            const shown = new Set(parsed.visibleKeys);
            setColumnPrefs(Object.fromEntries(tableColumns.map(col => [col.key, shown.has(col.key)])));
            setColumnsTouched(true);
        } else {
            // Show what a clause is filtering or sorting on (Kind is hidden by
            // default), unless the query named the columns itself. For this
            // visit only, and without counting as a layout choice: the bar
            // should keep reading as the query that was shared, not grow a
            // SHOW clause the reader never wrote.
            const used = new Set([...parsed.groups.flatMap(Object.keys), ...Object.keys(parsed.sorters)]);
            const hidden = tableColumns.filter(col => used.has(col.key) && !isColumnVisible(col));
            if (hidden.length > 0) {
                setColumnPrefs(prev => ({ ...prev, ...Object.fromEntries(hidden.map(col => [col.key, true])) }));
            }
        }
        if (parsed.unresolved.length > 0) {
            message.warning(`Ignored ${parsed.unresolved.join(', ')} — not available here.`);
        }
        return parsed;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableColumns, tabInQuery, stateFilter, columnPrefs]);

    // ...and apply one on the way in. The Kind column arrives with the rows
    // (and only on nodes with type identifiers), so a clause we can't place
    // yet may simply be early — wait for the fetches to settle before
    // concluding a title genuinely doesn't exist here.
    useEffect(() => {
        if (pendingQuery === null) return;
        const early = parseBrowseQuery(pendingQuery, { columns: tableColumns, tabs: STATE_TABS });
        if (early.unresolved.length > 0 && loading) return;
        applyQuery(pendingQuery);
        setPendingQuery(null);
    }, [pendingQuery, tableColumns, loading, applyQuery]);

    // The bar is editable so a query can be pasted or hand-written — the only
    // way to ask for OR. `null` means "not being edited", so it shows the live state.
    const [queryDraft, setQueryDraft] = useState(null);
    const [queryFocused, setQueryFocused] = useState(false);
    const commitQueryDraft = () => {
        if (queryDraft === null) return;
        const text = queryDraft.trim();
        if (text !== querySummary.trim()) {
            // Typed words with no operator in them aren't a malformed query,
            // they're a search: either end's name, OR'd. Committing rewrites
            // the bar in full syntax, so the grammar shows itself.
            applyQuery(text && !isQueryExpression(text)
                ? buildBrowseQuery({
                    columns: tableColumns,
                    groups: [{ a: [text] }, { b: [text] }],
                    tabLabel,
                })
                : text);
        }
        setQueryDraft(null);
    };

    const copyQueryLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            message.success({
                content: 'Link copied — send it to anyone. It reopens this search for them, over whatever they can see.',
                duration: 5,
            });
        } catch {
            message.error("Couldn't copy the link");
        }
    };

    // "Clear" only ever undoes filters and sort: the tab and the column layout
    // are the viewer's own place in the app, not part of a query they ran.
    const hasFilters = hasFilterClauses(filterGroups);
    const hasSort = Object.keys(tableSorters).length > 0;
    const clearLabel = !hasFilters ? 'Clear sort' : !hasSort ? 'Clear filters' : 'Clear all';
    const clearFiltersAndSort = () => {
        setFilterGroups([{}]);
        setTableSorters({});
        setQueryDraft(null);
    };

    // Bulk delete (superadmin, from the selection). The backend refuses a
    // group's last admin link with a 409 that names the group; that reason is
    // the one worth repeating, so the first failure's message is shown.
    const selectedEdges = useMemo(
        () => filteredEdges.filter(e => selectedRowKeys.includes(e.id)),
        [filteredEdges, selectedRowKeys],
    );
    const handleBulkDelete = async () => {
        setBulkActionBusy(true);
        const results = await Promise.allSettled(selectedEdges.map(e => api.del(`/api/connections/${e.id}`)));
        setBulkActionBusy(false);
        const failed = results.filter(r => r.status === 'rejected');
        const n = selectedEdges.length;
        if (failed.length > 0) {
            const why = failed[0].reason?.message;
            message.error(`Could not delete ${failed.length} of ${n}${why ? ` — ${why}` : ''}`);
        } else {
            message.success(`Deleted ${n} connection${n === 1 ? '' : 's'}`);
        }
        setBulkDeleteOpen(false);
        setSelectedRowKeys([]);
        fetchConnections();
    };

    // A row opens the connection in the same modal an entity's page uses,
    // looked at from one end: the group's, when there is one, since that's
    // the side whose admins accept requests and manage memberships. Who may
    // edit it is an admin of either end (your own person entity counts), so
    // both ends are checked once the modal is up; until then it's read-only
    // for everyone but a superadmin.
    const [selected, setSelected] = useState(null);
    const openEdge = useCallback((edge) => {
        const [viewpoint, other] = isGroup(edge.b) && !isGroup(edge.a) ? [edge.b, edge.a] : [edge.a, edge.b];
        const conn = { ...edge, connection_id: edge.id, entity: other };
        setSelected({ conn, viewpoint, isAdmin: isServerAdmin, canAccept: false });
        const adminOf = id => api.get(`/api/entities/${id}/admin-check`).then(d => !!d?.is_admin).catch(() => false);
        Promise.all([adminOf(viewpoint.id), adminOf(other.id)]).then(([viewpointAdmin, otherAdmin]) => {
            setSelected(prev => (prev?.conn.id === edge.id
                ? { ...prev, isAdmin: isServerAdmin || viewpointAdmin || otherAdmin, canAccept: isGroup(viewpoint) && viewpointAdmin }
                : prev));
        });
    }, [api, isServerAdmin]);

    return (
        <div className="browse-page" style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div>
                <div className="browse-header-row">
                    <Space wrap size="middle">
                        <div className="browse-segmented-scroll">
                            <Segmented
                                value={stateFilter}
                                // One more AND clause in the query, not a reset.
                                // Only the row selection is dropped, since
                                // those ids belong to the tab being left.
                                onChange={v => { setStateFilter(v); setSelectedRowKeys([]); }}
                                options={STATE_TABS.map(t => ({
                                    value: t.value,
                                    label: (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                                            {t.icon}
                                            {t.label}
                                        </span>
                                    ),
                                }))}
                            />
                        </div>
                        <Segmented
                            value={viewMode}
                            onChange={v => { setViewModePersisted(v); setSelectedRowKeys([]); }}
                            options={[
                                { value: 'table', icon: <TableOutlined /> },
                                { value: 'calendar', icon: <CalendarOutlined /> },
                            ]}
                        />
                        {/* Always rendered (disabled outside table view) so the
                            toolbar doesn't shift when switching views. */}
                        <Popover
                            open={viewMode === 'table' ? undefined : false}
                            trigger="click"
                            placement="bottomRight"
                            title="Columns"
                            content={(
                                <Space direction="vertical" size={4}>
                                    {tableColumns.map(col => (
                                        <Checkbox
                                            key={col.key}
                                            checked={isColumnVisible(col)}
                                            disabled={col.alwaysVisible}
                                            onChange={e => setColumnVisible(col.key, e.target.checked)}
                                        >
                                            {col.title}
                                        </Checkbox>
                                    ))}
                                </Space>
                            )}
                        >
                            <Tooltip title={viewMode === 'table' ? 'Choose columns' : 'Column settings apply to table view only'}>
                                <Button icon={<SettingOutlined />} aria-label="Choose columns" disabled={viewMode !== 'table'} />
                            </Tooltip>
                        </Popover>
                    </Space>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
                ) : loadError ? (
                    <Empty description={<Text type="secondary">Couldn't load connections. Try refreshing.</Text>} />
                ) : filteredEdges.length === 0 ? (
                    <Empty description={<Text type="secondary">Nothing here yet for this filter.</Text>} />
                ) : (
                    <>
                        <div className="browse-filter-banner">
                            <Input.TextArea
                                className="browse-query-input"
                                variant="borderless"
                                spellCheck={false}
                                autoSize={{ minRows: 1, maxRows: 4 }}
                                value={queryDraft ?? querySummary}
                                onChange={e => setQueryDraft(e.target.value)}
                                onFocus={() => setQueryFocused(true)}
                                onBlur={() => { setQueryFocused(false); commitQueryDraft(); }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitQueryDraft(); }
                                    if (e.key === 'Escape') { setQueryDraft(null); setQueryFocused(false); e.currentTarget.blur(); }
                                }}
                                placeholder={'Search, or write a query — Entity A contains "ali"  OR  State = "Pending"'}
                            />
                            <Space size={0}>
                                {queryDraft !== null && (
                                    <Button type="link" size="small" onClick={commitQueryDraft}>Apply</Button>
                                )}
                                {querySummary && (
                                    <Tooltip title="Copy a link to this search — filters, sort, tab and columns. Share it with anyone: they get the same search, run against what they're allowed to see.">
                                        <Button type="link" size="small" icon={<LinkOutlined />} onClick={copyQueryLink}>Copy link</Button>
                                    </Tooltip>
                                )}
                                {(hasFilters || hasSort) && (
                                    <Button type="link" size="small" onClick={clearFiltersAndSort}>{clearLabel}</Button>
                                )}
                            </Space>
                        </div>
                        {(queryFocused || queryDraft !== null) && (
                            <div className="browse-query-help">
                                {QUERY_HELP.map(([syntax, what]) => (
                                    <React.Fragment key={syntax}>
                                        <code>{syntax}</code>
                                        <Text type="secondary">{what}</Text>
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                        {viewMode === 'calendar' ? (
                            <BrowseCalendar
                                entities={calendarItems}
                                onOpen={item => openEdge(item.edge)}
                                nouns={['connection', 'connections']}
                                emptyHint={<>Give a connection a <strong>When</strong>, or pause it, and it shows up here.</>}
                            />
                        ) : (
                            <>
                                {isServerAdmin && selectedRowKeys.length > 0 && (
                                    <div className="browse-bulk-action-bar">
                                        <Text type="secondary">{selectedRowKeys.length} selected</Text>
                                        <Space size={8} wrap>
                                            <Button
                                                danger
                                                icon={<DeleteOutlined />}
                                                loading={bulkActionBusy}
                                                onClick={() => setBulkDeleteOpen(true)}
                                            >
                                                Delete ({selectedEdges.length})
                                            </Button>
                                        </Space>
                                    </div>
                                )}
                                {/* Scrolls sideways once dragged columns add up to more
                                    than the page is wide, instead of the whole page. */}
                                <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                                    <Table
                                        rowKey="id"
                                        size="small"
                                        pagination={false}
                                        components={TABLE_COMPONENTS}
                                        columns={visibleColumns}
                                        onChange={(_, filters, sorter) => { applyDropdownFilters(filters); applySorter(sorter); }}
                                        dataSource={visibleEdges}
                                        rowSelection={isServerAdmin ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
                                        onRow={(edge) => ({
                                            onClick: (e) => {
                                                // The checkbox column is for selecting, not opening.
                                                if (e.target.closest('.ant-table-selection-column')) return;
                                                openEdge(edge);
                                            },
                                            style: { cursor: 'pointer' },
                                        })}
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            <ConnectionEditModal
                open={!!selected}
                onClose={() => setSelected(null)}
                conn={selected?.conn ?? null}
                currentEntityId={selected?.viewpoint.id}
                isAdmin={!!selected?.isAdmin}
                canAcceptConnection={!!selected?.canAccept}
                userId={user?.id}
                onSuccess={() => { setSelected(null); fetchConnections(); }}
            />

            {isServerAdmin && (
                <Modal
                    title={`Delete ${selectedEdges.length} connection${selectedEdges.length === 1 ? '' : 's'}`}
                    open={bulkDeleteOpen}
                    onCancel={bulkActionBusy ? undefined : () => setBulkDeleteOpen(false)}
                    maskClosable={false}
                    okText="Delete"
                    okButtonProps={{ danger: true, icon: <DeleteOutlined />, loading: bulkActionBusy }}
                    onOk={handleBulkDelete}
                >
                    <Alert
                        type="error"
                        showIcon
                        style={{ marginBottom: 12 }}
                        title="This cannot be undone"
                        description="A member loses their place in the group and a pending request is denied. A group's last admin link is refused, so it keeps an admin."
                    />
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        {selectedEdges.map(edge => (
                            <div key={edge.id} style={{ padding: '2px 0' }}>
                                <Space size={8}>
                                    <Text>{edge.a.name}</Text>
                                    <Text type="secondary">↔</Text>
                                    <Text>{edge.b.name}</Text>
                                    {edge.role && <Tag style={{ textTransform: 'capitalize' }}>{edge.role}</Tag>}
                                </Space>
                            </div>
                        ))}
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default BrowseEdges;
